/**
 * funnelSteps
 * ───────────
 * Constructor compartido del embudo de 8 etapas usado por el StepperFunnel.
 * Originalmente vivía dentro de cotizacionesBuroApi.js — se extrajo para que
 * cotizacionesApi.js (Pool Trim) reutilice exactamente la misma lógica y los
 * steps del Pool dejen de aparecer "en blanco".
 */

export const FUNNEL_STAGES = [
    'Documentos',
    'Análisis',
    'Visita ocular',
    'Cotización',
    'Pago inicial',
    'Orden de compra',
    'Contrato',
    'Seguros',
    'Placas',
    // ── DIFERIDOS ── GPS se anexa a esta lista después.
];

const STEP_TYPE_TO_FUNNEL_IDX = {
    upload_documents:     0,
    ocular_visit:         2,
    confirm_quote:        3,
    upload_payment_proof: 4,
    request_invoice:      5,
    contract:             6,
    add_insurance:        7,
    register_plates:      8,
};

const STATUS_CODE_TO_FUNNEL_IDX = {
    docs_pending:  0,
    analysis:      1,
    ocular:        2,
    quoting:       3,
    paid:          4,
    invoiced:      5,
    contract_step: 6,
    insurance:     7,
    approved:      7,
    // 'contract' (legacy) cae a la etapa intermedia, ya no es estado final.
    contract:      6,
};

const STEP_LABEL_VERB = {
    ocular_visit:         'Visita ocular',
    confirm_quote:        'Cotización',
    upload_payment_proof: 'Pago inicial',
    request_invoice:      'Orden de compra',
    contract:             'Contrato',
    add_insurance:        'Seguros',
    register_plates:      'Placas',
};

const formatDate = (iso) => {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return null;
        return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return null;
    }
};

const buildTooltip = (i, activeIdx, statusCode, stepType, stepStatus, completedAt) => {
    const verb = STEP_LABEL_VERB[stepType] ?? FUNNEL_STAGES[i];
    if (i < activeIdx) {
        const fecha = formatDate(completedAt);
        return fecha ? `${FUNNEL_STAGES[i]} completada el ${fecha}` : `${FUNNEL_STAGES[i]} completada`;
    }
    if (i === activeIdx) {
        if (statusCode === 'rejected') {
            return stepType ? `${verb} marcada como no exitosa` : 'Cotización rechazada';
        }
        if (statusCode === 'approved') return 'Crédito aprobado — proceso completo';
        if (stepStatus === 'scheduled') return `${verb} agendada`;
        if (stepStatus === 'postponed') return `${verb} reprogramada`;
        if (stepStatus === 'in-progress') return `${verb} en curso`;
        if (stepStatus === 'completed' && completedAt) {
            return `${verb} completada el ${formatDate(completedAt)}, esperando siguiente paso`;
        }
        return `${verb} — paso actual`;
    }
    return `${FUNNEL_STAGES[i]} — pendiente`;
};

export const buildFunnelSteps = ({
    statusCode,
    currentStepType = null,
    currentStepStatus = null,
    currentStepCompletedAt = null,
    rejectedAtStep = null,
    docsHasWarnings = false,
    // Overlay del ciclo de vida del seguro sobre el último círculo (idx 7).
    // SOLO aplica cuando statusCode === 'approved'. No altera el latch del flujo;
    // el crédito sigue "Aprobado" — esto es info visual del Insurance.effective_status.
    insuranceEffectiveStatus = null,
    insuranceStartDate = null,
}) => {
    let activeIdx;
    if (statusCode === 'rejected') {
        activeIdx = STEP_TYPE_TO_FUNNEL_IDX[rejectedAtStep ?? currentStepType] ?? 1;
    } else if (currentStepType && STEP_TYPE_TO_FUNNEL_IDX[currentStepType] !== undefined) {
        activeIdx = STEP_TYPE_TO_FUNNEL_IDX[currentStepType];
    } else {
        activeIdx = STATUS_CODE_TO_FUNNEL_IDX[statusCode] ?? 1;
    }

    // Solo "approved" (crédito finalizado) marca TODAS las etapas en done.
    // "contract"/"contract_step"/"insurance" son etapas intermedias activas.
    if (statusCode === 'approved') {
        return FUNNEL_STAGES.map((label, i) => {
            let state = (i === 0 && docsHasWarnings) ? 'warn' : 'done';
            let tooltip = state === 'warn'
                ? 'Documentos completados con observaciones (algunos opcionales pendientes)'
                : buildTooltip(i, FUNNEL_STAGES.length, statusCode, currentStepType, currentStepStatus, currentStepCompletedAt);

            // Overlay del ciclo de vida del seguro sobre el último círculo (Seguros).
            // El hito sigue siendo "Aprobado" (latch intacto) — esto SOLO cambia
            // el badge visual y el tooltip cuando el Insurance vinculado no está
            // en estado `active`.
            if (i === 7) {
                if (insuranceEffectiveStatus === 'pending_activation') {
                    state = 'upcoming';
                    const fecha = formatDate(insuranceStartDate);
                    tooltip = fecha
                        ? `Seguros completado · próximo a iniciar el ${fecha}`
                        : 'Seguros completado · próximo a iniciar';
                } else if (insuranceEffectiveStatus === 'cancelled') {
                    state = 'cancelled';
                    tooltip = 'Seguros completado · póliza cancelada (puedes registrar una nueva)';
                } else if (insuranceEffectiveStatus === 'expired') {
                    state = 'expired';
                    tooltip = 'Seguros completado · vigencia vencida';
                } else if (insuranceEffectiveStatus === 'active') {
                    // Activo y vigente — mantiene done con tooltip enriquecido.
                    tooltip = 'Seguros completado · vigente';
                }
            }

            return { label, state, tooltip };
        });
    }

    return FUNNEL_STAGES.map((label, i) => {
        let state;
        if (statusCode === 'rejected') {
            if (i < activeIdx) state = 'done';
            else if (i === activeIdx) state = 'rej';
            else state = 'pending';
        } else if (i < activeIdx) {
            state = 'done';
        } else if (i === activeIdx) {
            state = 'active';
        } else {
            state = 'pending';
        }
        // Documentos completados con observaciones (warnings) → amarillo.
        if (i === 0 && state === 'done' && docsHasWarnings) {
            state = 'warn';
        }
        let tooltip = buildTooltip(i, activeIdx, statusCode, currentStepType, currentStepStatus, currentStepCompletedAt);
        if (state === 'warn') {
            tooltip = 'Documentos completados con observaciones (algunos opcionales pendientes)';
        }
        return {
            label,
            state,
            tooltip,
        };
    });
};
